// src/App.js
import React, { Suspense, lazy, useEffect, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Menu from './components/Menu';
import Footer from './components/Footer';
import './App.css';
import './responsive-fixes.css';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('App crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'sans-serif' }}>
          <h2 style={{ color: '#ef4444' }}>Une erreur est survenue</h2>
          <p style={{ color: '#64748b', marginBottom: '16px' }}>{this.state.error?.message}</p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/'; }}
            style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
          >
            Retour à l'accueil
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Composant ScrollToTop - Scroll vers le haut lors du changement de page
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Lazy loading simple sans instrumentation de debug
const createLazyComponent = (importFn) => {
  return lazy(importFn);
};

// Lazy loading des pages pour optimiser le bundle
// Lazy loading des pages pour optimiser le bundle
const HomePage = createLazyComponent(() => import('./pages/HomePage'));
const Login = createLazyComponent(() => import('./components/Login'));
const Register = createLazyComponent(() => import('./components/Register'));
const PasswordReset = createLazyComponent(() => import('./components/PasswordReset'));
const ProfilePage = createLazyComponent(() => import('./pages/ProfilePage'));
const Cart = createLazyComponent(() => import('./components/Cart'));
const Checkout = createLazyComponent(() => import('./components/Checkout'));
const PaymentSuccess = createLazyComponent(() => import('./components/PaymentSuccess'));
const PaymentError = createLazyComponent(() => import('./components/PaymentError'));
const ResetPassword = createLazyComponent(() => import('./components/ResetPassword'));
const EventDetails = createLazyComponent(() => import('./components/EventDetails'));
const EventsGrid = createLazyComponent(() => import('./components/EventsGrid'));
const ContactPage = createLazyComponent(() => import('./components/ContactPage'));
const RGPD = createLazyComponent(() => import('./components/RGPD'));
const MentionsLegales = createLazyComponent(() => import('./components/MentionsLegales'));
const HelloAssoPage = createLazyComponent(() => import('./components/HelloAssoPage'));
const PassesPage = createLazyComponent(() => import('./pages/PassesPage'));
const PassSelectionPage = createLazyComponent(() => import('./pages/PassSelectionPage'));
const ManagePassesPage = createLazyComponent(() => import('./pages/ManagePassesPage'));
const OrganizerOnboardingPage = createLazyComponent(() => import('./pages/OrganizerOnboardingPage'));
const StripeSuccessPage = createLazyComponent(() => import('./pages/StripeSuccessPage'));
const StripeRefreshPage = createLazyComponent(() => import('./pages/StripeRefreshPage'));
const OrganizerDashboardPage = createLazyComponent(() => import('./pages/OrganizerDashboardPage'));
const NotFound = createLazyComponent(() => import('./pages/NotFound'));

// Composant de chargement
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen" style={{ minHeight: '100vh' }}>
    <div 
      className="animate-spin rounded-full h-12 w-12 border-b-2" 
      style={{ 
        borderColor: '#2563eb',
        borderTopColor: 'transparent'
      }}
    ></div>
  </div>
);


function AppContent() {
  return (
      <AuthProvider>
        <CartProvider>
          <ScrollToTop />
          <div className="App">
            <Menu />
            <main>
            <Suspense fallback={<PageLoader />}>
                <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<PasswordReset />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/confirm-payment" element={<PaymentSuccess />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/success" element={<PaymentSuccess />} />
                <Route path="/error" element={<PaymentError />} />
                <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
                <Route path="/event/:eventId" element={<EventDetails />} />
                <Route path="/events" element={<EventsGrid />} />
                <Route path="/passes" element={<PassesPage />} />
                <Route path="/passes/:passId/selection" element={<PassSelectionPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/rgpd" element={<RGPD />} />
                <Route path="/mentions-legales" element={<MentionsLegales />} />
                <Route path="/helloasso" element={<HelloAssoPage />} />
                <Route path="/gestion-passes" element={<ManagePassesPage />} />
                <Route path="/organizer/onboarding" element={<OrganizerOnboardingPage />} />
                <Route path="/organizer/stripe-success" element={<StripeSuccessPage />} />
                <Route path="/organizer/stripe-refresh" element={<StripeRefreshPage />} />
                <Route path="/organizer/dashboard" element={<OrganizerDashboardPage />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </main>
            <Footer />
          {/* React Hot Toast - Position adaptative selon l'écran */}
          <Toaster
            position={typeof window !== 'undefined' && window.innerWidth < 768 ? 'bottom-center' : 'top-right'}
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#0f172a',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: '0 18px 55px rgba(15, 23, 42, 0.14)',
              },
              success: {
                iconTheme: {
                  primary: '#16a34a',
                  secondary: '#fff',
                },
              },
              error: {
                iconTheme: {
                  primary: '#ef4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          </div>
        </CartProvider>
      </AuthProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  );
}

export default App;
